var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { BwElement } from './base/bw-element.js';
// One id per field for its error message (the control's aria-describedby target).
let errorSeq = 0;
// Controls bw-field knows how to describe: native form controls and bw-input
// (whose host forwards aria-describedby to its inner input, like aria-invalid).
const DESCRIBABLE = 'input, select, textarea, bw-input';
// Add / remove ONE id in a space-separated aria-describedby, leaving anything the
// caller put there alone.
function toggleToken(el, token, on) {
    const tokens = (el.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean);
    const at = tokens.indexOf(token);
    if (on && at === -1)
        tokens.push(token);
    else if (!on && at !== -1)
        tokens.splice(at, 1);
    else
        return;
    if (tokens.length)
        el.setAttribute('aria-describedby', tokens.join(' '));
    else
        el.removeAttribute('aria-describedby');
}
let BwField = class BwField extends BwElement {
    constructor() {
        super(...arguments);
        this.label = '';
        this.caption = '';
        this.error = '';
        this.layout = 'stacked';
        this.disabled = false;
        // The error is announced by default (role="alert"): a new node with that role
        // is read out by a screen reader the moment it appears. That is right for a
        // fresh failure and wrong for a surface that REBUILDS its form (a re-render
        // after an unrelated update, a disclosure toggled): the recreated node would
        // read the same old error again. `error-quiet` renders the same text without
        // the role — the caller sets it on every render after the first one, and
        // drops it for a new attempt. The text stays tied to the control either way
        // (see `_describe`), so it is read when the field is focused.
        this.errorQuiet = false;
        this._errorId = `bw-field-error-${++errorSeq}`;
    }
    render() {
        return html `
      <label class="bw-field-row">
        <span class="bw-field-label">${this.label}</span>
        <span class="bw-field-control"><slot></slot></span>
      </label>
      ${this.error
            ? html `<span class="bw-field-error" id=${this._errorId}
                     role=${this.errorQuiet ? nothing : 'alert'}>${this.error}</span>`
            : this.caption
                ? html `<span class="bw-field-caption">${this.caption}</span>`
                : ''}
    `;
    }
    firstUpdated() { this.projectSlots(); }
    updated() { this._describe(); }
    // Tie the error text to the control (aria-describedby) while there is one.
    // Runs after projectSlots (firstUpdated precedes updated), so the control is
    // already inside `.bw-field-control`.
    _describe() {
        const control = this.querySelector('.bw-field-control');
        if (!control)
            return;
        for (const child of Array.from(control.children)) {
            if (child.matches(DESCRIBABLE))
                toggleToken(child, this._errorId, !!this.error);
        }
    }
};
__decorate([
    property({ type: String, reflect: true })
], BwField.prototype, "label", void 0);
__decorate([
    property({ type: String })
], BwField.prototype, "caption", void 0);
__decorate([
    property({ type: String })
], BwField.prototype, "error", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwField.prototype, "layout", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwField.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'error-quiet' })
], BwField.prototype, "errorQuiet", void 0);
BwField = __decorate([
    customElement('bw-field')
], BwField);
export { BwField };
//# sourceMappingURL=bw-field.js.map